/**
 * Profile → "Delete account". Apple requires that a customer can delete their
 * account from inside the app (App Store Guideline 5.1.1(v)); the same section
 * serves the website. With nothing open the account is erased at once; with
 * an active trip, Reserve plan, Defera Credit or collections case the request
 * goes to the team, who settle it with the customer first.
 * Server: server/account-deletion.js.
 */
import { useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Trash2, Loader2, X, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/lib/supabase';
import { REWARDS_SECTION_CARD } from '@/constants/brandColors';

export const CONFIRM_WORD = 'DELETE';

async function authed(path, init = {}) {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) throw new Error('Please sign in again.');
  const res = await fetch(path, {
    ...init,
    headers: { ...(init.headers || {}), Authorization: `Bearer ${token}`, ...(init.body ? { 'Content-Type': 'application/json' } : {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || 'Something went wrong. Please try again.');
  return body;
}

const money = (cents) => (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/** What still has to be settled, in the customer's words. */
export function blockerLine(b) {
  if (b.kind === 'trip') return b.ref ? `Your trip ${b.ref}, which is still active` : 'A trip that is still active';
  if (b.kind === 'reserve') return b.ref ? `Your Reserve plan (${b.ref})` : 'Your Reserve plan';
  if (b.kind === 'credit') return `${money(b.cents)} of Defera Credit`;
  if (b.kind === 'collections') return 'A balance that is with our collections team';
  return 'Something still open on your account';
}

/** The sheet's content for each step. Pure, so every state can be tested. */
export function DeleteAccountSheet({ step, info, typed, onType, onConfirm, onClose, error, working }) {
  if (step === 'loading') {
    return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-white/50" /></div>;
  }
  if (step === 'deleted') {
    return (
      <div className="text-center py-4">
        <CheckCircle className="w-9 h-9 text-emerald-300 mx-auto mb-3" />
        <p className="text-white text-[17px] font-medium">Your account has been deleted</p>
        <p className="text-white/60 text-[13.5px] font-normal mt-2 leading-relaxed">Thank you for flying with Defera. You&rsquo;ve been signed out.</p>
      </div>
    );
  }
  if (step === 'requested') {
    return (
      <div className="text-center py-4">
        <CheckCircle className="w-9 h-9 text-emerald-300 mx-auto mb-3" />
        <p className="text-white text-[17px] font-medium">We&rsquo;ve got your request</p>
        <p className="text-white/60 text-[13.5px] font-normal mt-2 leading-relaxed">
          Our team will contact you by email within 2 business days to settle what&rsquo;s still open, then delete your account.
        </p>
        <Button type="button" onClick={onClose} className="mt-5 rounded-full h-10 px-6 text-[13px]">Done</Button>
      </div>
    );
  }
  if (!info) return null;
  if (info.protected) {
    return <p className="text-white/70 text-[13.5px] font-normal leading-relaxed">This is a staff or partner account, so it can&rsquo;t be deleted here. Please contact support.</p>;
  }
  if (info.pending_request) {
    return (
      <p className="text-white/70 text-[13.5px] font-normal leading-relaxed">
        Your request to delete your account is in. Our team will contact you by email to finish closing it.
      </p>
    );
  }
  const now = info.can_delete_now;
  return (
    <div className="space-y-4">
      {now ? (
        <p className="text-white/80 text-[13.5px] font-normal leading-relaxed">
          This permanently deletes your Defera account. It can&rsquo;t be undone.
        </p>
      ) : (
        <div className="space-y-2">
          <p className="text-white/80 text-[13.5px] font-normal leading-relaxed">We need to settle a few things with you first:</p>
          <ul className="list-disc pl-5 space-y-1 text-white/75 text-[13.5px] font-normal">
            {info.blockers.map((b, i) => <li key={`${b.kind}-${i}`}>{blockerLine(b)}</li>)}
          </ul>
          <p className="text-white/60 text-[13px] font-normal leading-relaxed">
            Ask to delete your account and our team will contact you by email within 2 business days. For a trip, you can cancel it or keep it until you&rsquo;ve traveled. We delete your account as soon as everything is settled.
          </p>
        </div>
      )}
      <ul className="space-y-1.5 text-[13px] font-normal leading-relaxed">
        <li className="text-white/65"><span className="text-white/85">Deleted:</span> your profile, saved travelers, saved cards, linked cards, chats and preferences.</li>
        <li className="text-white/65"><span className="text-white/85">Kept:</span> records of your trips and payments, which the law requires us to keep.</li>
        {info.miles_balance > 0 && (
          <li className="text-white/65"><span className="text-white/85">Lost:</span> your {info.miles_balance.toLocaleString('en-US')} Defera Miles.</li>
        )}
      </ul>
      <div className="space-y-1.5">
        <label htmlFor="delete-confirm" className="text-white/60 text-[12.5px] font-normal">Type {CONFIRM_WORD} to confirm</label>
        <Input
          id="delete-confirm"
          value={typed}
          onChange={(e) => onType(e.target.value)}
          autoComplete="off"
          autoCapitalize="characters"
          className="bg-white/[0.04] border-white/15 text-white"
        />
      </div>
      {error && <p role="alert" className="rounded-xl border border-red-400/25 bg-red-500/10 px-3.5 py-2.5 text-[13px] text-red-200">{error}</p>}
      <Button
        type="button"
        onClick={onConfirm}
        disabled={typed.trim().toUpperCase() !== CONFIRM_WORD || working}
        className="w-full rounded-full h-11 text-[13.5px] bg-red-600 hover:bg-red-500 text-white disabled:opacity-40"
      >
        {working ? <Loader2 className="w-4 h-4 animate-spin" /> : now ? 'Delete my account' : 'Ask to delete my account'}
      </Button>
    </div>
  );
}
DeleteAccountSheet.propTypes = {
  step: PropTypes.string.isRequired,
  info: PropTypes.object,
  typed: PropTypes.string,
  onType: PropTypes.func,
  onConfirm: PropTypes.func,
  onClose: PropTypes.func,
  error: PropTypes.string,
  working: PropTypes.bool,
};

export default function DeleteAccountCard() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('loading');
  const [info, setInfo] = useState(null);
  const [typed, setTyped] = useState('');
  const [error, setError] = useState(null);
  const [working, setWorking] = useState(false);

  const openSheet = async () => {
    setOpen(true); setStep('loading'); setTyped(''); setError(null); setInfo(null);
    try {
      setInfo(await authed('/api/account/deletion'));
      setStep('confirm');
    } catch (e) {
      setInfo(null); setStep('confirm'); setError(e.message);
    }
  };
  const close = () => { if (!working) setOpen(false); };
  const confirm = async () => {
    setWorking(true); setError(null);
    try {
      const r = await authed('/api/account/deletion', { method: 'POST', body: JSON.stringify({ confirm: CONFIRM_WORD }) });
      if (r.status === 'deleted') {
        setStep('deleted');
        // The server already signed out every session; this clears this device.
        await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
        setTimeout(() => navigate('/', { replace: true }), 2500);
      } else {
        setStep('requested');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className={REWARDS_SECTION_CARD}>
      <div className="flex items-center justify-between gap-3 px-5 sm:px-6 py-5">
        <div className="min-w-0">
          <h3 className="text-white text-[15px] font-normal tracking-tight">Delete account</h3>
          <p className="text-white/40 text-[11.5px] font-normal">Permanently delete your Defera account and personal details</p>
        </div>
        <Button
          type="button"
          variant="ghost"
          onClick={openSheet}
          className="rounded-full h-9 px-4 text-xs text-red-300 hover:text-red-200 hover:bg-red-500/10 border border-red-400/25 flex-shrink-0"
        >
          <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete account
        </Button>
      </div>
      {open && createPortal(
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/70 p-0 sm:p-4" role="dialog" aria-modal="true" aria-labelledby="delete-account-title">
          <div className="w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl border border-white/10 bg-zinc-950 p-5 sm:p-6 pb-[max(1.25rem,env(safe-area-inset-bottom))]">
            <div className="flex items-center justify-between mb-4">
              <h2 id="delete-account-title" className="text-white text-[18px] font-medium">Delete your account</h2>
              {step !== 'deleted' && (
                <button type="button" onClick={close} aria-label="Close" className="p-1.5 rounded-full text-white/50 hover:text-white hover:bg-white/10">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <DeleteAccountSheet
              step={step}
              info={info}
              typed={typed}
              onType={setTyped}
              onConfirm={confirm}
              onClose={close}
              error={error}
              working={working}
            />
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
